/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
// src/pages/EventCalendar.tsx
import React, { useState, useEffect, useCallback, type JSX } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDarkMode } from "../context/DarkModeContext";
import { useAuth } from "../context/AuthContext";
import { eventService } from "../service/eventService";
import type { Event, EventType, EventAudience } from "../types/event";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
  Video,
  MapPin,
  Clock,
  Search,
  Edit,
  Trash2,
  UserCheck,
  X,
} from "lucide-react";

type CalendarView = "month" | "week" | "day" | "list";

const EventCalendar: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useDarkMode();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>("month");
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<EventType | "all">("all");
  const [audienceFilter, setAudienceFilter] = useState<EventAudience | "all">(
    "all",
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine if user is admin based on AuthContext
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  // Define loadEvents with useCallback to avoid infinite re-renders
  const loadEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: any = {};
      if (view === "month") {
        const start = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1,
        );
        const end = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0,
        );
        filters.startDate = start.toISOString();
        filters.endDate = end.toISOString();
      }

      const eventsData = await eventService.getEvents(filters);
      setEvents(eventsData);
    } catch (err) {
      setError("Failed to load events");
      console.error("Error loading events:", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentDate, view]);

  // Load events
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Filter events
  useEffect(() => {
    let filtered = events;

    if (searchTerm) {
      filtered = filtered.filter(
        (event) =>
          event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter((event) => event.type === typeFilter);
    }

    if (audienceFilter !== "all") {
      filtered = filtered.filter((event) => event.audience === audienceFilter);
    }

    // Filter out admin/internal events for non-admin users
    if (!isAdmin) {
      filtered = filtered.filter(
        (event) => event.audience !== "admin" && event.audience !== "internal",
      );
    }

    setFilteredEvents(filtered);
  }, [searchTerm, typeFilter, audienceFilter, events, isAdmin]);

  const handleEventRegister = async (eventId: string) => {
    try {
      await eventService.registerForEvent(eventId);
      await loadEvents(); // Reload events to update registration status
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(await eventService.getEvent(eventId));
      }
    } catch (err) {
      setError("Failed to register for event");
    }
  };

  const handleEventUnregister = async (eventId: string) => {
    try {
      await eventService.unregisterFromEvent(eventId);
      await loadEvents();
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(await eventService.getEvent(eventId));
      }
    } catch (err) {
      setError("Failed to unregister from event");
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await eventService.deleteEvent(eventId);
      await loadEvents();
      setSelectedEvent(null);
    } catch (err) {
      setError("Failed to delete event");
    }
  };

  // Fixed navigation handlers
  const handleCreateEvent = () => {
    navigate("/dashboard/create-event");
  };

  const handleEditEvent = (eventId: string) => {
    navigate(`/dashboard/edit-event/${eventId}`);
  };

  // Navigation functions
  const goToToday = () => setCurrentDate(new Date());
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const getEventColor = (type: EventType) => {
    const colors = {
      workshop: "bg-green-500",
      webinar: "bg-blue-500",
      networking: "bg-purple-500",
      qna: "bg-orange-500",
      conference: "bg-red-500",
      training: "bg-indigo-500",
      social: "bg-pink-500",
      other: "bg-gray-500",
    };
    return colors[type];
  };

  const renderMonthView = () => {
    const year: number = currentDate.getFullYear();
    const month: number = currentDate.getMonth();

    const firstDay: Date = new Date(year, month, 1);
    // const lastDay: Date = new Date(year, month + 1, 0);
    const startDate: Date = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const weeks: JSX.Element[] = [];
    let currentWeek: JSX.Element[] = [];
    const currentIterationDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const date = new Date(currentIterationDate);
      const dayEvents = filteredEvents.filter((event) => {
        const eventDate = new Date(event.startDate);
        return eventDate.toDateString() === date.toDateString();
      });

      currentWeek.push(
        <div
          key={date.toISOString()}
          className={`min-h-24 p-2 border ${
            isDark
              ? "border-gray-700 hover:bg-gray-800"
              : "border-blue-200 hover:bg-blue-50"
          } ${
            date.getMonth() !== month
              ? isDark
                ? "text-gray-600 bg-gray-900"
                : "text-gray-400 bg-blue-25"
              : isDark
                ? "text-gray-300"
                : "text-gray-700"
          }`}
        >
          <div className="flex justify-between items-center mb-1">
            <span
              className={`text-sm font-medium ${
                date.toDateString() === new Date().toDateString()
                  ? "bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                  : ""
              }`}
            >
              {date.getDate()}
            </span>
          </div>
          <div className="space-y-1">
            {dayEvents.slice(0, 2).map((event) => (
              <div
                key={event.id}
                className={`text-xs p-1 rounded cursor-pointer truncate ${getEventColor(
                  event.type,
                )} ${isDark ? "text-white" : "text-white"}`}
                onClick={() => setSelectedEvent(event)}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div
                className={`text-xs ${
                  isDark ? "text-gray-400" : "text-blue-600"
                }`}
              >
                +{dayEvents.length - 2} more
              </div>
            )}
          </div>
        </div>,
      );

      if (currentWeek.length === 7) {
        weeks.push(
          <div key={date.toISOString()} className="grid grid-cols-7 gap-0">
            {currentWeek}
          </div>,
        );
        currentWeek = [];
      }

      currentIterationDate.setDate(currentIterationDate.getDate() + 1);
    }

    return weeks;
  };

  const renderEventDetails = () => {
    if (!selectedEvent) return null;

    // Check if user can view this event (non-admin users can't view admin/internal events)
    if (
      !isAdmin &&
      (selectedEvent.audience === "admin" ||
        selectedEvent.audience === "internal")
    ) {
      return null;
    }

    return (
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${
          isDark ? "text-white" : "text-gray-900"
        }`}
      >
        <div
          className={`rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            isDark ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-2xl font-bold">{selectedEvent.title}</h3>
              <button
                onClick={() => setSelectedEvent(null)}
                className={`p-2 rounded-lg ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-400"
                    : "hover:bg-blue-100 text-blue-600"
                }`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className={`${isDark ? "text-gray-300" : "text-blue-700"}`}>
                {selectedEvent.description}
              </p>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Clock
                    className={`w-4 h-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                  />
                  <span>
                    {new Date(selectedEvent.startDate).toLocaleString()} -{" "}
                    {new Date(selectedEvent.endDate).toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <Video
                    className={`w-4 h-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                  />
                  <span>{selectedEvent.platform}</span>
                </div>

                {selectedEvent.location && (
                  <div className="flex items-center space-x-2">
                    <MapPin
                      className={`w-4 h-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                    />
                    <span>{selectedEvent.location}</span>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Users
                    className={`w-4 h-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                  />
                  <span>
                    {selectedEvent.registeredCount}
                    {selectedEvent.maxAttendees &&
                      ` / ${selectedEvent.maxAttendees}`}{" "}
                    registered
                  </span>
                </div>
              </div>

              {selectedEvent.meetingLink && (
                <a
                  href={selectedEvent.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold ${
                    isDark
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
                  }`}
                >
                  <Video className="w-4 h-4" />
                  <span>Join Event</span>
                </a>
              )}

              <div className="flex space-x-3 pt-4">
                {selectedEvent.isRegistered ? (
                  <button
                    onClick={() => handleEventUnregister(selectedEvent.id)}
                    className={`px-4 py-2 rounded-lg border ${
                      isDark
                        ? "border-red-500 text-red-400 hover:bg-red-900/20"
                        : "border-red-300 text-red-600 hover:bg-red-50"
                    }`}
                  >
                    Unregister
                  </button>
                ) : (
                  <button
                    onClick={() => handleEventRegister(selectedEvent.id)}
                    className={`px-4 py-2 rounded-lg ${
                      isDark
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-green-500 hover:bg-green-600 text-white"
                    }`}
                  >
                    Register Now
                  </button>
                )}

                {isAdmin && (
                  <>
                    <button
                      onClick={() => handleEditEvent(selectedEvent.id)} // Fixed: Use correct route
                      className={`px-4 py-2 rounded-lg border ${
                        isDark
                          ? "border-blue-500 text-blue-400 hover:bg-blue-900/20"
                          : "border-blue-300 text-blue-600 hover:bg-blue-50"
                      }`}
                    >
                      <Edit className="w-4 h-4 inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(selectedEvent.id)}
                      className={`px-4 py-2 rounded-lg border ${
                        isDark
                          ? "border-red-500 text-red-400 hover:bg-red-900/20"
                          : "border-red-300 text-red-600 hover:bg-red-50"
                      }`}
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDark ? "bg-gray-900" : "bg-gradient-to-br from-sky-50 to-blue-50"
        }`}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className={`mt-4 ${isDark ? "text-gray-400" : "text-blue-600"}`}>
            Loading events...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? 'bg-gray-900 text-gray-100'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900'
      }`}
    >
      <Helmet>
        <title>Job Events & Hiring Fairs in Kenya | FindWork</title>
        <meta
          name="description"
          content="Browse upcoming job fairs, hiring events, and career workshops near you in Kenya. Stay updated with FindWork events."
        />
        <link rel="canonical" href="https://findwork.ltd/events" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
          <div className="mb-4 lg:mb-0">
            <h1
              className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${
                isDark
                  ? 'from-blue-400 to-blue-600 text-white'
                  : 'from-sky-500 to-blue-600 text-transparent bg-clip-text'
              }`}
            >
              Event Calendar
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-blue-700'}`}>
              Stay updated with our latest events, workshops, and networking
              opportunities
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {isAdmin && (
              <button
                onClick={handleCreateEvent} // Fixed: Use correct route
                className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 ${
                  isDark
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span>Create Event</span>
              </button>
            )}
            <button
              onClick={goToToday}
              className={`px-4 py-2 rounded-lg border ${
                isDark
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
                  : 'border-blue-300 text-blue-600 hover:bg-blue-50'
              }`}
            >
              Today
            </button>
          </div>
        </div>

        {error && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              isDark
                ? 'bg-red-900/20 border border-red-800'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
          </div>
        )}

        {/* Calendar Controls */}
        <div
          className={`rounded-xl p-6 mb-6 ${
            isDark
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-blue-200 shadow-lg'
          }`}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPrevious}
                  className={`p-2 rounded-lg ${
                    isDark
                      ? 'hover:bg-gray-700 text-gray-400'
                      : 'hover:bg-blue-100 text-blue-600'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goToNext}
                  className={`p-2 rounded-lg ${
                    isDark
                      ? 'hover:bg-gray-700 text-gray-400'
                      : 'hover:bg-blue-100 text-blue-600'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <h2
                className={`text-xl font-bold ${
                  isDark ? 'text-white' : 'text-blue-800'
                }`}
              >
                {formatDate(currentDate)}
              </h2>
            </div>

            <div className="flex flex-wrap gap-2">
              {(['month', 'week', 'day', 'list'] as CalendarView[]).map(
                (viewType) => (
                  <button
                    key={viewType}
                    onClick={() => setView(viewType)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      view === viewType
                        ? isDark
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-500 text-white'
                        : isDark
                          ? 'text-gray-400 hover:bg-gray-700'
                          : 'text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    {viewType}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                  isDark ? 'text-gray-400' : 'text-blue-500'
                }`}
              />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-blue-200 text-gray-900 placeholder-blue-400'
                }`}
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as EventType | 'all')
              }
              className={`px-4 py-2 rounded-lg border ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-blue-200 text-gray-900'
              }`}
            >
              <option value="all">All Types</option>
              <option value="workshop">Workshop</option>
              <option value="webinar">Webinar</option>
              <option value="networking">Networking</option>
              <option value="qna">Q&A</option>
              <option value="conference">Conference</option>
              <option value="training">Training</option>
              <option value="social">Social</option>
              <option value="other">Other</option>
            </select>

            <select
              value={audienceFilter}
              onChange={(e) =>
                setAudienceFilter(e.target.value as EventAudience | 'all')
              }
              className={`px-4 py-2 rounded-lg border ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-blue-200 text-gray-900'
              }`}
            >
              <option value="all">All Audiences</option>
              <option value="public">Public</option>
              <option value="community">Community</option>
              <option value="premium">Premium</option>
              {isAdmin && <option value="admin">Admin</option>}
              {isAdmin && <option value="internal">Internal</option>}
            </select>
          </div>
        </div>

        {/* Calendar View */}
        <div
          className={`rounded-xl overflow-hidden ${
            isDark
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-blue-200 shadow-lg'
          }`}
        >
          {/* Month View Header */}
          {view === 'month' && (
            <div className="grid grid-cols-7 gap-0 border-b">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className={`p-4 text-center font-semibold ${
                    isDark
                      ? 'border-gray-700 text-gray-300'
                      : 'border-blue-200 text-blue-800'
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>
          )}

          {/* Calendar Content */}
          <div className="min-h-[600px]">
            {view === 'month' && renderMonthView()}
            {view === 'list' && (
              <div className="p-6">
                <div className="space-y-4">
                  {filteredEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        isDark
                          ? 'border-gray-700 hover:bg-gray-750'
                          : 'border-blue-200 hover:bg-blue-50'
                      }`}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                        <div className="flex-1">
                          <h3
                            className={`font-semibold ${isDark ? 'text-white' : 'text-blue-800'}`}
                          >
                            {event.title}
                          </h3>
                          <p
                            className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-blue-600'}`}
                          >
                            {event.description}
                          </p>
                          <div
                            className={`flex flex-wrap gap-4 mt-2 text-sm ${isDark ? 'text-gray-500' : 'text-blue-500'}`}
                          >
                            <span className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>
                                {new Date(event.startDate).toLocaleString()}
                              </span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Video className="w-4 h-4" />
                              <span>{event.platform}</span>
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                isDark
                                  ? 'bg-blue-600/20 text-blue-400'
                                  : 'bg-blue-100 text-blue-600'
                              }`}
                            >
                              {event.type}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 sm:mt-0 sm:ml-4 flex items-center space-x-2">
                          <span
                            className={`text-sm ${isDark ? 'text-gray-400' : 'text-blue-600'}`}
                          >
                            {event.registeredCount} registered
                          </span>
                          {event.isRegistered && (
                            <UserCheck
                              className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredEvents.length === 0 && (
                    <div
                      className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-blue-500'}`}
                    >
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No events found matching your criteria</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Details Modal */}
        {renderEventDetails()}
      </div>
    </div>
  );
};

export default EventCalendar;
